import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { usePortalAuth } from '../../context/PortalAuthContext';

/**
 * Admin → "View as customer" handoff page.
 *
 * Receives a base64-encoded payload in `?h=` with the freshly-minted
 * portal token + customer email + timestamp. Writes the credentials
 * into the portal session via `PortalAuthContext.setSession()` (which
 * persists to localStorage AND updates React state so the protected
 * routes see the new token synchronously), then redirects to
 * /portal/catalog.
 *
 * The payload is single-use: the URL is replaced immediately so the
 * admin can't accidentally share the link, and a 5-minute freshness
 * check prevents stale links from working.
 */
const PortalImpersonate = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setSession } = usePortalAuth();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const raw = params.get('h');
        if (!raw) {
            toast.error('Missing impersonation payload');
            navigate('/portal/login', { replace: true });
            return;
        }
        let payload;
        try {
            payload = JSON.parse(window.atob(decodeURIComponent(raw)));
            if (!payload.token || !payload.email) throw new Error('Invalid payload');
        } catch {
            toast.error('Invalid impersonation payload');
            navigate('/portal/login', { replace: true });
            return;
        }
        // Freshness — drop anything older than 5 minutes.
        if (Date.now() - (payload.ts || 0) > 5 * 60 * 1000) {
            toast.error('Impersonation link expired — please re-issue from the admin');
            navigate('/portal/login', { replace: true });
            return;
        }
        // Tag the session so the portal UI can render a visible
        // impersonation banner once that feature ships.
        sessionStorage.setItem('uc.portal.impersonated', '1');
        // Persist AND update React state in one shot — this is the
        // critical difference from the previous implementation which
        // wrote localStorage directly and left the context state stale
        // (causing PortalProtectedRoute to bounce back to /portal/login).
        Promise.resolve(setSession({ token: payload.token, email: payload.email })).then(() => {
            // Land the admin on the catalog page with a replace-nav so
            // the impersonation payload never lingers in browser history.
            // (We used to also `history.replaceState('', '', '/portal')`
            //  first, but that fought react-router and the follow-up
            //  navigate() would silently no-op, stranding users on /portal.)
            toast.success(`Viewing as ${payload.email}`);
            navigate('/portal/catalog', { replace: true });
        });
    }, [location.search, navigate, setSession]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#FBF8F6]" data-testid="portal-impersonate-page">
            <div className="bg-white border border-[#EFE5E1] rounded-lg p-8 max-w-sm w-full text-center">
                <div className="w-12 h-12 rounded-full bg-[#FBF8F6] border border-[#EFE5E1] flex items-center justify-center mx-auto mb-3">
                    <Shield size={20} className="text-[#C08478]" />
                </div>
                <p className="text-sm font-semibold text-[#1A1A1A]">Switching session…</p>
                <p className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1.5">
                    <Loader2 size={11} className="animate-spin" /> Opening the B2B portal as the selected customer
                </p>
            </div>
        </div>
    );
};

export default PortalImpersonate;
